import { useFrame } from '@react-three/fiber';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import * as THREE from 'three';
import { useShallow } from 'zustand/shallow';
import { useThirdPersonCamera } from '../../../hooks/useThirdPersonCamera';
import { useGameStore } from '../../../store/gameStore';
import CollisionManager from '../../../utils/CollisionManager';
import PlayerHealth from './PlayerHealth';
import PlayerPhysics from './PlayerPhysics';

export const PlayerModel = forwardRef(
  ({ scene, camera, showHitbox = false }, ref) => {
    const localRef = useRef();
    const damageRef = useRef(0);
    const playerRef = useRef();
    const coneHelperRef = useRef();
    useThirdPersonCamera(playerRef);
    const addPlayerBleed = useGameStore(
      useShallow((state) => state.addPlayerBleed)
    );

    useImperativeHandle(ref, () => localRef.current);

    useEffect(() => {
      if (localRef.current && showHitbox) {
        // Create cone frustum geometry
        const coneHeight = 1.7;
        const topRadius = 0.3;
        const bottomRadius = 0.3;

        // Create a custom geometry for the cone frustum
        const geometry = new THREE.CylinderGeometry(
          bottomRadius,
          topRadius,
          coneHeight,
          16
        );
        geometry.rotateX(Math.PI); // Flip to match your collision calculation

        // Create mesh for the cone
        const material = new THREE.MeshBasicMaterial({
          color: 0xff0000,
          wireframe: true,
          transparent: true,
          opacity: 0.7,
        });

        const coneMesh = new THREE.Mesh(geometry, material);

        // Position the cone relative to the player (center offset)
        coneMesh.position.y = 1.75 - coneHeight / 2;

        // Create a helper group to manage the cone position
        const helper = new THREE.Group();
        helper.add(coneMesh);
        coneHelperRef.current = helper;

        // Add to scene
        scene.parent?.add(helper);

        return () => {
          scene.parent?.remove(helper);
        };
      }
    }, [scene, showHitbox]);

    useFrame(() => {
      if (!localRef.current || !camera.userData.characterPos) return;

      // Update model position based on physics position
      localRef.current.position.set(
        camera.userData.characterPos.x,
        camera.userData.characterPos.y - 1.5,
        camera.userData.characterPos.z
      );

      if (camera.userData.lastAngle !== undefined) {
        localRef.current.rotation.y = camera.userData.lastAngle;
      }

      // Update cone helper position to follow the player
      if (coneHelperRef.current) {
        coneHelperRef.current.position.copy(localRef.current.position);
        coneHelperRef.current.rotation.y = localRef.current.rotation.y;
      }
    });

    const playerTakeDamage = (d) => {
      damageRef.current += d.damage;
      d.direction && addPlayerBleed(d.position, d.direction, d.damage);
    };
    useEffect(() => {
      if (localRef.current)
        CollisionManager.registerPlayer(localRef, playerTakeDamage);

      return () => {
        // Cleanup when component unmounts
        CollisionManager.unregisterPlayer &&
          CollisionManager.unregisterPlayer();
      };
    }, [localRef]);

    return (
      <>
        <PlayerPhysics playerRef={playerRef} />
        <primitive
          ref={localRef}
          object={scene}
          position={[0, 0, 5]}
          scale={[1, 1, 1]}
          castShadow
        />
        <PlayerHealth modelRef={ref} damageRef={damageRef} />
      </>
    );
  }
);
